import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '~/services/api';
import { IFile } from '~/services/modules/contract/types';
import useStore, { storeState } from '~/store';
import { getErrorMessage, logErrorWithContext } from '~/utils/errorHandler';
import { reportError } from '~/utils/reportError';
import { DocumentType } from '~/constants/documentType';
import { documentTypeMap } from '../constants/documentTypeMap';
import { ContractResponseAPI } from './contractResponse';
import { ContractFlowHookResult, FilesError, FilesState, TypeFile } from './types';

export const useEmailFlow = (cid: string | null): ContractFlowHookResult => {
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('org') ?? undefined;
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [errors, setErrors] = useState<FilesError>({} as FilesError);
  const [publicContract, setPublicContract] = useState<any>(null);
  const [files, setFiles] = useState<FilesState>({
    invoice: null,
    overview: null,
    serial_number_photo: null,
    ratchets_group: null,
    other: null
  });
  const [documentUrls, setDocumentUrls] = useState<Record<string, string>>({});
  const [hasVoucherError, setHasVoucherError] = useState(false);
  const [submitError, setSubmitError] = useState<{
    title: string;
    message: string;
    type: 'error' | 'success' | 'warning';
  } | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<{
    title: string;
    message: string;
    type: 'error' | 'success' | 'warning';
  } | null>(null);

  // Obtém os métodos da store necessários para atualização dos dados
  const {
    setContractId,
    setHasInvoice,
    setIsNewEquipment,
    setParticipatesInCompetitions,
    setEquipmentMaterial,
    setInternationalCoverage,
    setTotalValue,
    setServiceCharge,
    setCameFromEmail,
    contractId
  } = useStore((store) => ({
    setContractId: store.setContractId,
    contractId: store.contract_id,
    setHasInvoice: store.setHasInvoice,
    setIsNewEquipment: store.setIsNewEquipment,
    setParticipatesInCompetitions: store.setParticipatesInCompetitions,
    setEquipmentMaterial: store.setEquipmentMaterial,
    setInternationalCoverage: store.setInternationalCoverage,
    setTotalValue: store.setTotalValue,
    setServiceCharge: store.setServiceCharge,
    setCameFromEmail: store.setCameFromEmail
  }));

  // Função para buscar documentos existentes (contractHash = id do contrato em hash)
  const fetchExistingDocuments = async (contractHash: string) => {
    try {
      const params = organizationId ? { organization_id: organizationId } : undefined;
      const response = await api.v2!.get(`Contracts/${contractHash}/insured_items/documents`, {
        params
      });
      if (response?.data?.data) {
        const docUrls: Record<string, string> = {};
        const documents = response.data.data.documents || [];
        documents.forEach((doc: any) => {
          const typeInfo = documentTypeMap[doc.document_type_id as DocumentType];
          if (typeInfo) {
            docUrls[typeInfo.key] = doc.path;
          }
        });
        setDocumentUrls(docUrls);
      }
    } catch (error) {
      console.error('Erro ao buscar documentos existentes:', error);
    }
  };

  // Carregar dados do contrato público e atualizar a store
  useEffect(() => {
    if (cid) {
      setLoading(true);
      const getParams = organizationId ? { params: { organization_id: organizationId } } : undefined;
      api.v2
        ?.get<ContractResponseAPI>(`/Contracts/${cid}`, getParams)
        .then((response) => {
          const contractData = response.data.data;

          // Guardar dados na variável local para uso na interface
          setPublicContract(contractData);

          // Atualizar o ID do contrato na store
          setContractId(contractData.contract_id);

          // Indicar que está vindo do fluxo de email
          setCameFromEmail(true);

          // Atualizar dados do insured_item na store
          const { insured_item } = contractData;
          setHasInvoice(insured_item.has_invoice);
          setIsNewEquipment(insured_item.is_new);
          setParticipatesInCompetitions(insured_item.participates_competition);
          setEquipmentMaterial(insured_item.equipment_material_type);
          setInternationalCoverage(insured_item.international_coverage === 1);

          // Atualizar valores financeiros
          setTotalValue(contractData.total);
          setServiceCharge(contractData.financial_charges);

          // Atualizar os demais dados diretamente na store
          storeState.insured = {
            ...contractData.insured,
            account_activated: false,
            // Garantir que additional_details nunca seja null
            address: {
              ...contractData.insured.address,
              additional_details: contractData.insured.address.additional_details || ''
            }
          };

          storeState.insured_item = {
            type_insured_item: contractData.insured_item.type_insured_item,
            manufacturer: contractData.insured_item.manufacturer,
            model: contractData.insured_item.model,
            serial_number: contractData.insured_item.serial_number,
            year_of_manufacture: contractData.insured_item.year_of_manufacture,
            insured_amount: contractData.insured_item.insured_amount,
            total_value_accessories: contractData.insured_item.total_value_accessories,
            is_new: contractData.insured_item.is_new,
            has_invoice: contractData.insured_item.has_invoice,
            participates_competition: contractData.insured_item.participates_competition,
            equipment_material_type: contractData.insured_item.equipment_material_type,
            international_coverage: contractData.insured_item.international_coverage,
            equipment_type: contractData.insured_item.equipment_type,
            equipment_type_id: contractData.insured_item.equipment_type_id
          };

          // Atualizar dados do plano
          storeState.plan = contractData.plan;
          storeState.has_club = contractData.plan.has_club;
          storeState.club_name = contractData.plan.club_name;
          storeState.club_description = contractData.plan.club_description;
          storeState.flat_rate = contractData.flat_rate;

          // Indicar que os dados foram carregados com sucesso
          setDataLoaded(true);

          // Buscar documentos existentes usando o hash do contrato (cid)
          fetchExistingDocuments(cid);
        })
        .catch((error) => {
          setHasVoucherError(true);

          const errorContext = logErrorWithContext(error, 'email_flow_load_contract', {
            cid
          });

          const errorResponse = getErrorMessage(error);
          // Não chamar storeState.addToast aqui, deixar para o componente pai
          setSubmitError(errorResponse);

          reportError(`Erro ao carregar contrato no fluxo de email: ${errorResponse.title}`, error, {
            context: 'email_flow_load_contract',
            cid,
            errorType: errorContext.errorType
          });
        })
        .finally(() => setLoading(false));
    }
  }, [
    cid,
    organizationId,
    contractId,
    setContractId,
    setHasInvoice,
    setIsNewEquipment,
    setParticipatesInCompetitions,
    setEquipmentMaterial,
    setInternationalCoverage,
    setTotalValue,
    setServiceCharge,
    setCameFromEmail
    // fetchExistingDocuments
  ]);

  // Extrair informações do contrato
  const hasInvoice = publicContract?.insured_item?.has_invoice || false;
  const isNew = publicContract?.insured_item?.is_new || false;
  const product = publicContract?.insured_item?.equipment_type_id;
  // Extrair product_ids dos produtos no plano
  const productId = publicContract?.plan?.products?.map((p: { product_id: number }) => p.product_id) || [];

  // Handler para alteração de arquivo
  const onChangeFile = (key: TypeFile, value: IFile) => {
    setFiles((state) => ({
      ...state,
      [key]: value
    }));
    setErrors((state) => ({ ...state, [key]: false }));
  };

  // Handler para submissão
  const onSubmit = async () => {
    if (cid) {
      try {
        setLoading(true);
        setSubmitError(null);
        setSubmitSuccess(null);

        const filesToUpload: Record<string, IFile> = {};

        Object.entries(files).forEach(([key, value]) => {
          if (value) {
            filesToUpload[key] = value;
          }
        });

        const orgParam = searchParams.get('org');
        const payload = orgParam ? { ...filesToUpload, organization_id: orgParam } : filesToUpload;
        await api.v2!.post(`Contracts/${cid}/documents/mod-int`, payload);

        setLoading(false);
        setSubmitSuccess({
          title: 'Sucesso',
          message: 'Arquivos enviados com sucesso! Sua solicitação será analisada pela equipe.',
          type: 'success'
        });
      } catch (error: any) {
        setLoading(false);

        const errorContext = logErrorWithContext(error, 'email_flow_upload_files', {
          contractId,
          cid,
          filesCount: Object.keys(files).length,
          hasNotifications: error.isAxiosError && !!error.response?.data?.notifications
        });

        const errorResponse = getErrorMessage(error);
        setSubmitError(errorResponse);

        reportError(`Erro ao processar arquivos no fluxo de email: ${errorResponse.title}`, error, {
          context: 'email_flow_upload_files',
          contractId,
          cid,
          errorType: errorContext.errorType,
          filesCount: Object.keys(files).length,
          originalNotifications: error.isAxiosError ? error.response?.data?.notifications : undefined
        });
      }
    }
  };

  const isEmailMode = !!cid;

  return {
    loading,
    dataLoaded,
    files,
    errors,
    hasInvoice,
    isNew,
    product,
    productId,
    hasVoucherError,
    documentUrls,
    isEmailMode,
    submitError,
    submitSuccess,
    onChangeFile,
    onSubmit
  };
};
